import React, { useEffect, useState } from 'react';
import { Grid, Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RocketIcon from '@mui/icons-material/Rocket';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Container } from '@mui/material';

// Custom styles for different screen sizes
const CultureComponent = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const is4K = useMediaQuery("(min-width: 3840px)");
  const is900to1024 = useMediaQuery('(min-width:900px) and (max-width:1224px)');
  
  // Dynamic font sizes based on screen size
  const fontSizes = {
    heroTitle: isXs ? 40 : isSm ? 42 : isMd ? 42 : 42,
    heroSubtitle: isXs ? 16 : isSm ? 18 : isMd ? 18 : 18,
    cardTitle: isXs ? 22 : isSm ? 25 : isMd ? 25 : 25,
    cardBody: isXs ? 16 : isSm ? 18 : isMd ? 18 : 18,
    sectionTitle: isXs ? 24 : isSm ? 24 : isMd ? 24 : 24,
  };

  // Dynamic icon sizes based on screen size
  const iconSize = isXs ? 50 : isSm ? 51 : isMd ? 51 : 52;

  const cards = [
    {
      id: 1,
      title: "Collaboration & Teamwork",
      content: "We know the best results come when people work together. At DevAngles, we make sure everyone's ideas matter, and we work side by side with respect and open minds. Whether it's brainstorming or fixing a challenge, teamwork is how we get things done and build strong connections.",
      icon: <GroupIcon sx={{ fontSize: iconSize, color: '#3AADCE' }} />
    },
    {
      id: 2,
      title: "Innovation at the Core",
      content: "At DevAngles, we don't just talk about innovation — it's part of how we work every day. We love trying new ideas and using the latest tech to solve real problems. Everyone here is encouraged to think outside the box and find better ways to do things.",
      icon: <LightbulbIcon sx={{ fontSize: iconSize, color: '#3AADCE' }} />
    },
    {
      id: 3,
      title: "Growth & Learning",
      content: "Your growth matters to us. We offer plenty of chances to learn, get support from mentors, and pick up new skills. We believe staying curious and open to learning helps you and the whole team keep up with the fast-changing tech world.",
      icon: <TrendingUpIcon sx={{ fontSize: iconSize, color: '#3AADCE' }} />
    }
  ];

  const [zoomLevel, setZoomLevel] = useState('normal');

  useEffect(() => {
    const detectZoom = () => {
      const ratio = window.outerWidth / window.innerWidth;

      if (ratio > 1) {
        setZoomLevel('zoomIn'); // 110% to 200%
      } else if (ratio < 0.9) {
        setZoomLevel('zoomOut'); // 50% to 90%
      } else {
        setZoomLevel('normal'); // ~100%
      }
    };

    detectZoom();
    window.addEventListener('resize', detectZoom);

    return () => window.removeEventListener('resize', detectZoom);
  }, []);

  // Dynamic padding based on zoom level
  const getPaddingValues = () => {
    if (zoomLevel === 'zoomIn') {
      // For 110% to 200% zoom - use smaller padding
      return {
       paddingLeft: is4K ? '1200px' : is900to1024 ? '51px' : '210px',
        paddingRight: is4K ? '1200px' : is900to1024 ? '50px' : '210px'
      };
    } else if (zoomLevel === 'zoomOut') {
      // For 50% to 90% zoom - use larger padding
      return {
        paddingLeft: is4K ? '1200px' : is900to1024 ? '51px' : '63px',
        paddingRight: is4K ? '1200px' : is900to1024 ? '50px' : '65px'
      };
    } else {
      // Normal zoom - use default padding
      return {
        paddingLeft: is4K ? '1200px' : is900to1024 ? '51px' : '63px',
        paddingRight: is4K ? '1200px' : is900to1024 ? '50px' : '65px'
      };
    }
  };

  const { paddingLeft, paddingRight } = getPaddingValues();
  
  return (
    <Box sx={{
      pl: { xs: 2, sm: 2, md: paddingLeft },
      pr: { xs: 2, sm: 0, md: paddingRight },
      pb: { xs: 4, sm: 6, md: 8 },
      ...(zoomLevel === 'zoomOut' && {
        maxWidth: 'xl',
        mx: 'auto',
      }),
      ...(zoomLevel === 'zoomIn' && {
        maxWidth: '100%',
      }),
      ...(zoomLevel === 'normal' && {
        maxWidth: 'xl',
        mx: 'auto',
      }),
    }}>
      {/* Section Title */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography 
          sx={{ 
            color: "#0087B4",
            fontSize: fontSizes.cardBody,
            fontFamily: 'Lexend',
            mb: 1,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          Our Culture
        </Typography>
        
        {/* Hero Title */}
        <Typography 
          variant="h1" 
          component="h1" 
          sx={{ 
            fontWeight: 400,
            fontSize: fontSizes.heroTitle,
            fontFamily: 'Lexend',
            lineHeight: 1.2,
            mb: { xs: 4, sm: 6, md: 8 },
            px: { xs: 1, sm: 2, md: 4 }
          }}
        >
          Building A Culture Of Innovation Where Technology Experts Collaborate To Solve Tomorrow's Challenges.
        </Typography>
      </Box>
      
      {/* Cards Grid */}
      <Grid container spacing={4}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={12} md={4} key={card.id}>
            <Box 
              sx={{
                p: { xs: 3, sm: 4, md: 3, lg: 4 },
                border: '1px solid #eee',
                borderRadius: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                  backgroundColor: '#F8FBFD'
                }
              }}
            >
              {/* Icon */}
              <Box 
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <Box
                  sx={{
                    width: 85,
                    height: 85,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(58, 173, 206, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
              
              {/* Card Title */}
              <Typography 
                variant="h3" 
                component="h3"
                sx={{ 
                  fontWeight: 400,
                  fontSize: fontSizes.cardTitle,
                  fontFamily: 'Lexend, sans-serif',
                  mb: 2,
                  textAlign: 'center'
                }}
              >
                {card.title}
              </Typography>
              
              {/* Card Content */}
              <Typography 
                sx={{ 
                  fontSize: fontSizes.cardBody,
                  fontFamily: 'Lexend, sans-serif',
                  lineHeight: 1.6,
                  textAlign: 'center',
                  fontWeight: 300,
                  color:'#000000DE'
                }}
              >
                {card.content}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CultureComponent;